import React from 'react';
import axios from 'axios';
import { last } from 'ramda';
import { Box, useToast } from '@chakra-ui/react';
import { useEnvironment } from '#core/Environment';
import { useInstitution } from '#core/Institution';
import { usePageQuery } from '#core/PageQuery';
import { EventType, useTracking } from '#core/Tracking';
import useLogger from '#hooks/useLogger';
// JME
// import { useAuthHook } from '#utils/useAuthHook';
// JME1
import { useAuth } from '#core/Auth';

const DownloadLink: React.FC<{
  url: string;
  fileType: string;
  params?: { [key: string]: any };
  children: React.ReactNode;
}> = ({ url, fileType, params, children }) => {
  const { apiEndpoint } = useEnvironment();
  const { apiHeaders: authApiHeaders, permissionToken } = useAuth(); // JME2
  const { institution, apiHeaders: institutionApiHeaders } = useInstitution();
  const { setDownloadInProgress } = usePageQuery();
  const { trackEvent } = useTracking();
  const logger = useLogger();
  const toast = useToast();

  const handleClick = () => {
    setDownloadInProgress(true);
    trackEvent({
      type: EventType.DOWNLOAD,
      payload: { id: url, fileType },
    });
    const fetchFile = async () => {
      try {
        const response = await axios.get(`https://${apiEndpoint}${url}`, {
          responseType: 'blob',
          // Increase the timeout to 3 minutes so that the large graphql request has time to complete.
          timeout: 3 * 60 * 1000,
          params: {
            ...params,
            allowScoresForExcluded: !!params?.isWurReporterInterim,
            institutionId: institution?.institutionId,
          },
          headers: {
            ...authApiHeaders,
            ...institutionApiHeaders,
            'permissions-token': permissionToken,
          },
        });

        const contentBlob = new Blob([response.data]);
        const objectUrl = window.URL.createObjectURL(contentBlob);
        const fileName = last(
          response.headers['content-disposition']?.split('='),
        );
        const anchor = document.createElement('a');
        document.body.appendChild(anchor);
        anchor.download = fileName;
        anchor.href = objectUrl;
        anchor.click();
        window.URL.revokeObjectURL(objectUrl);
      } catch (error) {
        logger.error('Could not fetch selected file.');

        toast({
          title: 'There was a problem downloading your data. Please try again.',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
          status: 'error',
        });
      } finally {
        setDownloadInProgress(false);
      }
    };

    fetchFile();
  };

  return (
    <Box
      display="flex"
      width="100%"
      justifyContent="space-between"
      onClick={handleClick}
    >
      {children}
    </Box>
  );
};

export default DownloadLink;
